import React, { useState, useEffect, useRef } from "react";
import accountingReportsApi from "../../api/accountingReportsApi";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import {
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ReportPrintHeader from "../../components/ReportPrintHeader.jsx";

export default function BalanceSheetReport() {
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const printRef = useRef();

  const handleSearch = async () => {
    setLoading(true);
    try {
      const res = await accountingReportsApi.getBalanceSheet({
        asOfDate,
      });
      setReportData(res.data);
    } catch (error) {
      console.error(error);
      toast.error("فشل جلب الميزانية العمومية");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `BalanceSheet-${new Date().toISOString()}`,
  });

  const SectionTable = ({ title, data, total, color }) => (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="bold" color={color} gutterBottom>
        {title}
      </Typography>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>رقم الحساب</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>اسم الحساب</TableCell>
              <TableCell align="left" sx={{ fontWeight: "bold" }}>المبلغ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.accountId || Math.random()} hover>
                <TableCell>{row.accountCode}</TableCell>
                <TableCell>{row.accountName}</TableCell>
                <TableCell align="left">{row.amount.toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>
                  لا توجد بيانات
                </TableCell>
              </TableRow>
            )}
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <TableCell colSpan={2} align="right" sx={{ fontWeight: "bold" }}>
                إجمالي {title}
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                {total.toLocaleString()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box p={3} dir="rtl">
      <Typography variant="h4" fontWeight="bold" mb={3} color="primary">
        🏛️ الميزانية العمومية (Balance Sheet)
      </Typography>

      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="كما في تاريخ"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2} display="flex" gap={1}>
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                fullWidth
                sx={{ height: 56 }}
              >
                {loading ? "..." : "بحث"}
              </Button>
              {reportData && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handlePrint}
                  sx={{ height: 56, minWidth: 56 }}
                >
                  <PrintIcon />
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {reportData && (
        <div ref={printRef} style={{ padding: "20px", backgroundColor: "white" }}>
          
          <Box>
             <ReportPrintHeader 
                title="الميزانية العمومية"
                dateRange={`كما في ${new Date(asOfDate).toLocaleDateString("en-GB")}`}
             />
             <style>{`
                @media print {
                   .no-print { display: none !important; }
                   /* Ensure table header backgrounds print */
                   thead { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
                   th { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
                   /* Force layout to be visible */
                   .MuiGrid-root { display: flex !important; }
                }
             `}</style>
          </Box>

          <Grid container spacing={4}>
            {/* الأصول - يمين */}
            <Grid item xs={6}>
              <SectionTable 
                title="الأصول (Assets)" 
                data={reportData.assets} 
                total={reportData.totalAssets} 
                color="#1976d2"
              />
            </Grid>

            {/* الخصوم وحقوق الملكية - يسار */}
            <Grid item xs={6}>
              <SectionTable 
                title="الخصوم (Liabilities)" 
                data={reportData.liabilities} 
                total={reportData.totalLiabilities} 
                color="#c62828"
              />
              
              <SectionTable 
                title="حقوق الملكية (Equity)" 
                data={reportData.equity} 
                total={reportData.totalEquity} 
                color="#f57c00"
              />

              <Box 
                p={2} 
                bgcolor="#eeeeee" 
                borderRadius={2}
                textAlign="center"
                border={1}
                borderColor="#bdbdbd"
                mt={2}
                sx={{ 
                  "&": {
                     WebkitPrintColorAdjust: "exact",
                     printColorAdjust: "exact"
                  }
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  إجمالي الخصوم وحقوق الملكية
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="#333">
                  {reportData.totalLiabilitiesAndEquity.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          {/* Check Balance */}
          <Box mt={4} textAlign="center" className="no-print">
             {Math.abs(reportData.totalAssets - reportData.totalLiabilitiesAndEquity) < 0.01 ? (
                <Typography color="green" fontWeight="bold">✅ الميزانية متوازنة</Typography>
             ) : (
                <Typography color="red" fontWeight="bold">❌ الميزانية غير متوازنة (الفرق: {(reportData.totalAssets - reportData.totalLiabilitiesAndEquity).toLocaleString()})</Typography>
             )}
          </Box>
        </div>
      )}
    </Box>
  );
}
